import { useCallback, useEffect, useState } from 'react';
import { normalizeTickerList } from '../constants/watchlistDefaults';

export const FAVORITE_TICKERS_KEY = 'pt:favoriteTickers';
export const FAVORITE_TICKERS_EVENT = 'pt:favoriteTickers';
export const FAVORITE_STAR_COLOR = '#E8C547';

export function loadFavoriteTickers() {
  try {
    return normalizeTickerList(JSON.parse(localStorage.getItem(FAVORITE_TICKERS_KEY) || '[]'));
  } catch (_) {
    return [];
  }
}

export function saveFavoriteTickers(tickers) {
  const next = normalizeTickerList(tickers);
  try {
    localStorage.setItem(FAVORITE_TICKERS_KEY, JSON.stringify(next));
  } catch (_) { /* ignore quota / private mode */ }
  return next;
}

export function isFavoriteTicker(ticker, favorites = loadFavoriteTickers()) {
  const symbol = String(ticker || '').trim().toUpperCase();
  return Boolean(symbol) && favorites.includes(symbol);
}

export function toggleFavoriteTicker(ticker, current = loadFavoriteTickers()) {
  const symbol = String(ticker || '').trim().toUpperCase();
  if (!symbol) return current;
  const next = current.includes(symbol)
    ? current.filter((item) => item !== symbol)
    : [...current, symbol];
  return saveFavoriteTickers(next);
}

export function useFavoriteTickers() {
  const [favorites, setFavorites] = useState(loadFavoriteTickers);

  useEffect(() => {
    const sync = () => {
      const next = loadFavoriteTickers();
      setFavorites((prev) => (
        prev.length === next.length && prev.every((ticker, index) => ticker === next[index])
          ? prev
          : next
      ));
    };
    window.addEventListener('storage', sync);
    window.addEventListener(FAVORITE_TICKERS_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(FAVORITE_TICKERS_EVENT, sync);
    };
  }, []);

  const toggleFavorite = useCallback((ticker) => {
    const next = toggleFavoriteTicker(ticker, loadFavoriteTickers());
    setFavorites(next);
    window.dispatchEvent(new Event(FAVORITE_TICKERS_EVENT));
    return next;
  }, []);

  const isFavorite = useCallback(
    (ticker) => isFavoriteTicker(ticker, favorites),
    [favorites],
  );

  return { favorites, toggleFavorite, isFavorite };
}
